<template>
  <v-navigation-drawer
    :value="isDrawerOpen"
    app
    temporary
    width="260"
    class="app-navigation-menu d-lg-none"
    :right="$i18n.locale === 'ar' ? true : false"
    @input="val => $emit('update:is-drawer-open', val)"
  >
    <!-- Navigation Header -->
    <div class="vertical-nav-header d-flex items-center ps-6 pe-5 pt-5 pb-2">
      <router-link
        to="/"
        class="d-flex align-center text-decoration-none"
      >
        <v-img
          :src="require('@/assets/images/logos/logo.png')"
          max-height="40px"
          max-width="120px"
          alt="logo"
          contain
          eager
          class="app-logo me-2"
        ></v-img>
      </router-link>
    </div>

    <!-- Navigation Items -->
    <!-- Customer Service Navigation (role = 5): Approved screens "الرئيسية", "العملاء", and "الحجوزات" -->
    <v-list
      v-if="isCustomerService"
      expand
      shaped
      class="vertical-nav-menu-items pr-5"
    >
      <nav-menu-link
        title="العملاء"
        to="/customer-service/customers"
        icon="mdi-account-group"
      ></nav-menu-link>
      <nav-menu-link
        title="الحجوزات"
        to="/customer-service/bookings"
        icon="mdi-ticket-confirmation-outline"
      ></nav-menu-link>
      <nav-menu-link
        title="الاستردادات"
        to="/customer-service/refunds"
        icon="mdi-cash-refund"
      ></nav-menu-link>
      <nav-menu-link
        title="التذاكر"
        to="/customer-service/tickets"
        icon="mdi-ticket-account"
      ></nav-menu-link>
    </v-list>

    <!-- Operations Navigation (role = 4): ONLY approved screen "الرئيسية" -->
    <v-list
      v-else-if="isOperations"
      expand
      shaped
      class="vertical-nav-menu-items pr-5"
    >
      <nav-menu-link
        title="الرئيسية"
        :to="{ name: 'operations-home' }"
        icon="mdi-view-dashboard-outline"
      ></nav-menu-link>
      <nav-menu-link
        title="السائقين"
        :to="{ name: 'operations-drivers' }"
        icon="mdi-card-account-details-outline"
      ></nav-menu-link>
      <nav-menu-link
        title="وسائل النقل"
        :to="{ name: 'operations-vehicles' }"
        icon="mdi-bus"
      ></nav-menu-link>
      <nav-menu-link
        title="المسارات"
        :to="{ name: 'operations-routes' }"
        icon="mdi-routes"
      ></nav-menu-link>
      <nav-menu-link
        title="الرحلات"
        :to="{ name: 'operations-trips' }"
        icon="mdi-bus-clock"
      ></nav-menu-link>
      <nav-menu-link
        title="التتبع المباشر"
        :to="{ name: 'operations-live-tracking' }"
        icon="mdi-crosshairs-gps"
      ></nav-menu-link>
      <nav-menu-link
        title="الدعم والتذاكر"
        :to="{ name: 'operations-support' }"
        icon="mdi-headset"
      ></nav-menu-link>
    </v-list>

    <!-- Route Making Navigation (role = 3): ONLY approved screens "المسارات" and "الرحلات" -->
    <v-list
      v-else-if="isRouteMaking"
      expand
      shaped
      class="vertical-nav-menu-items pr-5"
    >
      <nav-menu-link
        :title="$t('Routes')"
        :to="{ name: 'routes' }"
        icon="mdi-road-variant"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('trips')"
        :to="{ name: 'trips' }"
        icon="mdi-bus-clock"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Trip Groups')"
        :to="{ name: 'trip-groups' }"
        icon="mdi-folder-multiple-outline"
      ></nav-menu-link>
    </v-list>

    <!-- Super Admin Navigation (role = 0): Unchanged -->
    <v-list
      v-else-if="isSuperAdmin"
      expand
      shaped
      class="vertical-nav-menu-items pr-5"
    >
      <nav-menu-link
        :title="$t('dashboard')"
        :to="{ name: 'dashboard' }"
        :icon="icons.mdiHomeOutline"
      ></nav-menu-link>
      <nav-menu-section-title :title="$t('USERS')"></nav-menu-section-title>
      <nav-menu-group
        :title="$t('Users')"
        icon="mdi-account-group"
      >
      <nav-menu-link
        :title="$t('Customers')"
        :to="{ name: 'customers' }"
        icon="mdi-account"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Drivers')"
        :to="{ name: 'drivers' }"
        icon="mdi-account-tie-hat"
      ></nav-menu-link>
      </nav-menu-group>
      <nav-menu-section-title :title="$t('SYSTEM SETUP')"></nav-menu-section-title>
      <nav-menu-link
        :title="$t('Buses')"
        :to="{ name: 'buses' }"
        icon="mdi-bus-multiple"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Routes')"
        :to="{ name: 'routes' }"
        icon="mdi-road-variant"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Stops')"
        :to="{ name: 'stops' }"
        icon="mdi-bus-stop"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Trips')"
        :to="{ name: 'trips' }"
        icon="mdi-bus-clock"
      ></nav-menu-link>
      <nav-menu-group
        :title="$t('Private Car Booking')"
        icon="mdi-car"
      >
        <nav-menu-link
          :title="$t('Car Types')"
          :to="{ name: 'car-types' }"
          icon="mdi-shape-outline"
        ></nav-menu-link>
        <nav-menu-link
          :title="$t('Cars')"
          :to="{ name: 'cars' }"
          icon="mdi-car-info"
        ></nav-menu-link>
        <nav-menu-link
          :title="$t('Private Car Requests')"
          :to="{ name: 'private-car-requests' }"
          icon="mdi-file-document-outline"
        ></nav-menu-link>
      </nav-menu-group>
      <nav-menu-group
        :title="$t('driver_services')"
        icon="mdi-tow-truck"
      >
        <nav-menu-link
          :title="$t('towing_services')"
          :to="{ name: 'towing-services' }"
          icon="mdi-tow-truck"
        ></nav-menu-link>
        <nav-menu-link
          :title="$t('repair_centers')"
          :to="{ name: 'repair-centers' }"
          icon="mdi-wrench-clock"
        ></nav-menu-link>
        <nav-menu-link
          :title="$t('driver_faqs')"
          :to="{ name: 'driver-faqs' }"
          icon="mdi-help-circle-outline"
        ></nav-menu-link>
      </nav-menu-group>
      <nav-menu-section-title :title="$t('LIVE')"></nav-menu-section-title>
      <nav-menu-link
        :title="$t('Live Tracking')"
        :to="{ name: 'live-tracking' }"
        icon="mdi-crosshairs-gps"
        ></nav-menu-link>
      <nav-menu-section-title :title="$t('REPORTS')"></nav-menu-section-title>
      <nav-menu-link
        :title="$t('Time Table')"
        :to="{ name: 'planned-trips' }"
        icon="mdi-airplane-clock"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Reservations')"
        :to="{ name: 'reservations' }"
        icon="mdi-poll"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Complaints')"
        :to="{ name: 'complaints' }"
        icon="mdi-comment-alert"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Driver Conflicts')"
        :to="{ name: 'driver-conflicts' }"
        icon="mdi-alert-circle-outline"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Emergency End Requests')"
        :to="{ name: 'driver-emergency-requests' }"
        icon="mdi-alert-octagon"
      ></nav-menu-link>
      <nav-menu-section-title :title="$t('REGULAR SUBSCRIPTIONS')"></nav-menu-section-title>
      <nav-menu-group
        :title="$t('Regular Subscriptions')"
        icon="mdi-wallet-membership"
      >
        <nav-menu-link
          :title="$t('Subscription Plans')"
          :to="{ name: 'subscription-plans' }"
          icon="mdi-card-text-outline"
        ></nav-menu-link>
        <nav-menu-link
          :title="$t('Route Passes')"
          :to="{ name: 'route-subscription-passes' }"
          icon="mdi-card-account-details-outline"
        ></nav-menu-link>
        <nav-menu-link
          :title="$t('Trip Subscriptions')"
          :to="{ name: 'trip-subscriptions' }"
          icon="mdi-account-card-outline"
        ></nav-menu-link>
      </nav-menu-group>
      <nav-menu-section-title :title="$t('COUPONS')"></nav-menu-section-title>
      <nav-menu-link
        :title="$t('Coupons')"
        :to="{ name: 'coupons' }"
        icon="mdi-ticket-percent"
      ></nav-menu-link>
      <nav-menu-section-title :title="$t('PAYMENTS')"></nav-menu-section-title>
      <nav-menu-link
        :title="$t('Upcoming Payments')"
        :to="{ name: 'upcoming-payments' }"
        icon="mdi-clock-outline"
      ></nav-menu-link>
      <nav-menu-link
        :title="$t('Redemptions')"
        :to="{ name: 'redemptions' }"
        icon="mdi-cash-multiple"
      ></nav-menu-link>
      <nav-menu-section-title :title="$t('SETTINGS')"></nav-menu-section-title>
      <nav-menu-link
        :title="$t('Settings')"
        :to="{ name: 'settings' }"
        icon="mdi-cog"
      ></nav-menu-link>
      <!-- Privacy policy -->
      <nav-menu-link
        :title="$t('Privacy Policy')"
        :to="{ name: 'privacy-policy' }"
        icon="mdi-shield-account"
      ></nav-menu-link>
      <!-- Terms and Conditions -->
      <nav-menu-link
        :title="$t('Terms & Conditions')"
        :to="{ name: 'terms-and-conditions' }"
        icon="mdi-file-document-outline"
      ></nav-menu-link>
      <br/>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import {
  mdiHomeOutline,
  mdiAlphaTBoxOutline,
  mdiEyeOutline,
  mdiCreditCardOutline,
  mdiTable,
  mdiFileOutline,
  mdiFormSelect,
  mdiAccountCogOutline,
  mdiMapMarkerPath,
  mdiCrosshairsGps
} from '@mdi/js'
import NavMenuSectionTitle from './components/NavMenuSectionTitle.vue'
import NavMenuGroup from './components/NavMenuGroup.vue'
import NavMenuLink from './components/NavMenuLink.vue'
import {Keys} from '/src/config.js'
import { getUserRole } from '@/services/AuthService'

export default {
  components: {
    NavMenuSectionTitle,
    NavMenuGroup,
    NavMenuLink,
    Keys
  },
  props: {
    isDrawerOpen: {
      type: Boolean,
      default: null,
    },
  },
  computed: {
    isSuperAdmin() {
      return getUserRole() === 0;
    },
    isRouteMaking() {
      return getUserRole() === 3;
    },
    isOperations() {
      return getUserRole() === 4;
    },
    isCustomerService() {
      return getUserRole() === 5;
    },
  },
  setup() {
    return {
      icons: {
        mdiHomeOutline,
        mdiAlphaTBoxOutline,
        mdiEyeOutline,
        mdiCreditCardOutline,
        mdiTable,
        mdiFileOutline,
        mdiFormSelect,
        mdiAccountCogOutline,
        mdiMapMarkerPath,
        mdiCrosshairsGps
      },
      systemName: Keys.VUE_APP_SYSTEM_NAME
    }
  },
}
</script>

<style lang="scss" scoped>
.app-title {
  font-size: 1.25rem;
  font-weight: 700;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.3px;
}

// ? Adjust this `translateX` value to keep logo in center when vertical nav menu is collapsed (Value depends on your logo)
.app-logo {
  transition: all 0.18s ease-in-out;
  .v-navigation-drawer--mini-variant & {
    transform: translateX(-4px);
  }
}

@include theme(app-navigation-menu) using ($material) {
  background-color: map-deep-get($material, 'background');
}

.app-navigation-menu {
  .v-list-item {
    &.vertical-nav-menu-link {
      ::v-deep .v-list-item__icon {
        .v-icon {
          transition: none !important;
        }
      }
    }
  }
}

// You can remove below style
// Upgrade Banner
.app-navigation-menu {
  .upgrade-banner {
    position: absolute;
    bottom: 13px;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
